import type { Item } from './drawing';
import { dxfSheets } from './dxfImport';
import { SHEET } from './geometry';
import { t as $t } from './i18n';
import { newId } from './tagging';
import type { Page, Underlay } from './types';

/*
 * Imported drawings behind a page: an old DXF schematic is placed on new
 * pages (one per sheet found in the file), scaled into the drawing area, to be
 * traced over with EnerCAD symbols. The underlay is pure graphics on its own
 * layer and never takes part in connectivity or reports.
 */

/** Drawing area of the frame (inside the column header, above the title block). */
const AREA = { x0: SHEET.X0 + 2, x1: SHEET.X1 - 2, y0: SHEET.Y0 + SHEET.HDR + 2, y1: SHEET.TB_Y - 2 };
/** Points kept per page; larger drawings lose their last polylines (with a warning). */
export const MAX_UNDERLAY_POINTS = 150_000;
const MAX_PAGES = 60;
const r1 = (v: number) => Math.round(v * 10) / 10;

/** New schematic pages, one per sheet of the DXF, each carrying the sheet as an underlay. */
export function dxfUnderlayPages(text: string, fileName: string): { pages: Page[]; warnings: string[] } {
  const { sheets, warnings } = dxfSheets(text);
  const base = fileName.replace(/\.(dxf|dwg)$/i, '').slice(0, 80) || 'DXF';
  if (sheets.length > MAX_PAGES) warnings.push($t("Dosyada {0} pafta bulundu; ilk {1} tanesi alındı.", sheets.length, MAX_PAGES));
  const pages = sheets.slice(0, MAX_PAGES).map((sh, i) => {
    const [bx0, by0, bx1, by1] = sh.bb;
    const w = Math.max(bx1 - bx0, 1e-6), h = Math.max(by1 - by0, 1e-6);
    const k = Math.min((AREA.x1 - AREA.x0) / w, (AREA.y1 - AREA.y0) / h);
    const ox = AREA.x0 + (AREA.x1 - AREA.x0 - w * k) / 2, oy = AREA.y0 + (AREA.y1 - AREA.y0 - h * k) / 2;
    // DXF y points up, the page's y down
    const X = (x: number) => r1(ox + (x - bx0) * k), Y = (y: number) => r1(oy + (by1 - y) * k);
    const u: Underlay = { name: fileName.slice(0, 120), lines: [], circles: [], texts: [] };
    let points = 0, cut = false;
    for (const g of sh.geo) {
      if (g.k === 'line') {
        const p: number[] = [];
        for (const q of g.pts) {
          const x = X(q[0]), y = Y(q[1]);
          if (p.length && p[p.length - 2] === x && p[p.length - 1] === y) continue; // collapsed at this scale
          p.push(x, y);
        }
        if (p.length < 4) continue;
        if (points + p.length / 2 > MAX_UNDERLAY_POINTS) {
          cut = true;
          continue;
        }
        points += p.length / 2;
        u.lines.push({ p, ...(g.closed ? { c: 1 as const } : {}), ...(g.dash ? { d: 1 as const } : {}) });
      } else if (g.k === 'circle') {
        const r = r1(g.r * k);
        if (r > 0) u.circles.push([X(g.c[0]), Y(g.c[1]), r]);
      } else if (g.text.trim()) {
        const hh = r1(g.h * k);
        // a clockwise angle on the y-down page is the DXF's counter-clockwise one, negated
        if (hh > 0) u.texts.push({ x: X(g.p[0]), y: Y(g.p[1]), h: hh, s: g.text.slice(0, 500), ...(g.rot ? { r: r1(-g.rot) } : {}) });
      }
    }
    if (cut) warnings.push($t("{0}. paftada çizim çok yoğun; bazı çizgiler alınmadı.", i + 1));
    const desc = sheets.length > 1 ? `${base} (${i + 1})` : base;
    return { id: newId(), desc, underlay: u, syms: [], wires: [] } as Page;
  });
  if (!pages.length) warnings.push($t("DXF dosyasında çizim bulunamadı."));
  return { pages, warnings };
}

/** Display items of an underlay: thin, muted, on the "underlay" layer. */
export function underlayItems(u: Underlay): Item[] {
  const out: Item[] = [];
  for (const l of u.lines) {
    const pts: [number, number][] = [];
    for (let i = 0; i + 1 < l.p.length; i += 2) pts.push([l.p[i], l.p[i + 1]]);
    out.push({ k: 'poly', pts, closed: !!l.c, stroke: 'ink2', width: 0.18, ...(l.d ? { dash: [1.5, 1] as [number, number] } : {}), layer: 'underlay' });
  }
  for (const [x, y, r] of u.circles) out.push({ k: 'circle', c: [x, y], r, stroke: 'ink2', width: 0.18, layer: 'underlay' });
  // DXF heights are cap heights; the display font size is about 1.4 times that
  for (const t of u.texts) out.push({ k: 'text', x: t.x, y: t.y, size: r1(t.h * 1.4), text: t.s, anchor: 'start', font: 'ui', colour: 'ink2', ...(t.r ? { rot: t.r } : {}), layer: 'underlay' });
  return out;
}

/** Validates a stored underlay; undefined when there is nothing usable. */
export function normaliseUnderlay(v: unknown): Underlay | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const u = v as any;
  const n = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
  let points = 0;
  const lines: Underlay['lines'] = [];
  for (const l of Array.isArray(u.lines) ? u.lines : []) {
    if (!Array.isArray(l?.p) || l.p.length < 4) continue;
    const p = l.p.slice(0, (l.p.length >> 1) << 1).map(n);
    if (points + p.length / 2 > MAX_UNDERLAY_POINTS) break;
    points += p.length / 2;
    lines.push({ p, ...(l.c ? { c: 1 as const } : {}), ...(l.d ? { d: 1 as const } : {}) });
  }
  const circles = (Array.isArray(u.circles) ? u.circles : []).slice(0, 50_000).filter((c: unknown) => Array.isArray(c) && c.length === 3).map((c: unknown[]) => [n(c[0]), n(c[1]), n(c[2])] as [number, number, number]);
  const texts = (Array.isArray(u.texts) ? u.texts : [])
    .slice(0, 50_000)
    .filter((t: any) => typeof t?.s === 'string' && t.s)
    .map((t: any) => ({ x: n(t.x), y: n(t.y), h: n(t.h), s: t.s.slice(0, 500), ...(n(t.r) ? { r: n(t.r) } : {}) }));
  if (!lines.length && !circles.length && !texts.length) return undefined;
  return { name: typeof u.name === 'string' ? u.name.slice(0, 120) : '', lines, circles, texts };
}
